import type { AgGridColumnFilter } from "./ag-grid-filters";
import type {
  GridRowGroupCol,
  GridSortModel,
  GridValueCol,
} from "./grid-request";
import type { StructuredQueryConfig } from "./types";

export type SsrmGridSlice = {
  rowGroupCols: GridRowGroupCol[];
  valueCols: GridValueCol[];
  sortModel: GridSortModel[];
  filterModel: Record<string, AgGridColumnFilter | null | undefined>;
};

/** Minimal SSRM getRows request shape (no ag-grid dependency). */
export type SsrmGetRowsRequestSlice = {
  rowGroupCols?: ReadonlyArray<{ field?: string | null; id?: string | null }>;
  valueCols?: ReadonlyArray<{
    field?: string | null;
    id?: string | null;
    aggFunc?: string | null;
  }>;
  sortModel?: ReadonlyArray<{ colId: string; sort: "asc" | "desc" }>;
  filterModel?: Record<string, AgGridColumnFilter | null | undefined> | null;
};

export function ssrmSliceFromRequest(
  req: SsrmGetRowsRequestSlice,
): SsrmGridSlice {
  return {
    rowGroupCols: (req.rowGroupCols ?? []).map((col) => ({
      field: col.field ?? col.id ?? "",
      id: col.id ?? col.field ?? "",
    })),
    valueCols: (req.valueCols ?? []).map((col) => ({
      field: col.field ?? col.id ?? "*",
      id: col.id ?? col.field ?? undefined,
      aggFunc: col.aggFunc ?? undefined,
    })),
    sortModel: (req.sortModel ?? []).map((sort) => ({
      colId: sort.colId,
      sort: sort.sort,
    })),
    filterModel: req.filterModel ?? {},
  };
}

/** SSRM often sends `{}` for filterModel; use grid API filters when the request is empty. */
export function resolveSsrmFilterModel(
  requestFilterModel: SsrmGetRowsRequestSlice["filterModel"],
  apiFilterModel:
    | Record<string, AgGridColumnFilter | null | undefined>
    | null
    | undefined,
): Record<string, AgGridColumnFilter | null | undefined> {
  if (requestFilterModel && Object.keys(requestFilterModel).length > 0) {
    return requestFilterModel;
  }
  if (apiFilterModel && Object.keys(apiFilterModel).length > 0) {
    return apiFilterModel;
  }
  return {};
}

export function structuredConfigToGetRowsInput(
  config: StructuredQueryConfig,
): Omit<StructuredQueryConfig, "kind"> {
  return {
    entity: config.entity,
    columns: config.columns,
    groupBy: config.groupBy,
    measures: config.measures,
    filters: config.filters,
    dateRange: config.dateRange,
    sort: config.sort,
    limit: config.limit,
    offset: config.offset,
  };
}
